import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardFooterComponent } from '../../../shared/card-footer/card-footer.component';
import { HeaderComponent } from '../../../shared/header/header.component';
import { PasswordInputComponent } from '../../../shared/password-input/password-input.component';
import { EmployeeAuthService } from '../../../shared/services/employee-auth.service';
import { ErrorMessageService } from '../../../shared/services/error-message.service';
@Component({
    selector: 'app-change-password',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        HeaderComponent,
        CardFooterComponent,
        PasswordInputComponent,
        TranslateModule,
    ],
    templateUrl: './change-password.component.html',
    styleUrl: './change-password.component.css',
})
export class ChangePasswordComponent implements OnInit {
    newPassword = '';
    confirmPassword = '';
    loading = false;
    errorMessage = '';
    sessionId = '';
    showSuccessScreen = false; // Add for success screen

    // Touch states
    newPasswordTouched = false;
    confirmPasswordTouched = false;

    // Password strength
    passwordStrength = '';
    passwordStrengthText = '';

    constructor(
        private titleService: Title,
        private router: Router,
        private translate: TranslateService,
        private employeeAuthService: EmployeeAuthService,
        private errorMessageService: ErrorMessageService
    ) { }

    ngOnInit(): void {
        this.titleService.setTitle('Employee | Change Password');
        this.translate.use(this.translate.currentLang || 'en');
        // Get session data from session storage or redirect back if not available
        const sessionDataString = sessionStorage.getItem('employeeSessionData') || '';
        if (!sessionDataString) {
            this.router.navigate(['/employee/account/password']);
            return;
        }
        const sessionData = JSON.parse(sessionDataString);
        this.sessionId = sessionData.sessionId || '';
        if (!this.sessionId) {
            this.router.navigate(['/employee/account/password']);
        }
    }

    validateNewPassword(): void {
        this.validatePassword();
    }

    validatePassword(): void {
        // Match signup.component.ts logic
        if (!this.newPassword) {
            this.passwordStrength = '';
            this.passwordStrengthText = '';
            return;
        }
        const hasUpperCase = /[A-Z]/.test(this.newPassword);
        const hasLowerCase = /[a-z]/.test(this.newPassword);
        const hasNumbers = /\d/.test(this.newPassword);
        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.newPassword);
        const hasMinLength = this.newPassword.length >= 8;
        const requirementsMet = [
            hasUpperCase,
            hasLowerCase,
            hasNumbers,
            hasSpecialChar,
            hasMinLength,
        ].filter(Boolean).length;
        if (this.newPassword.length >= 8 && requirementsMet >= 4) {
            if (requirementsMet === 5) {
                this.passwordStrength = 'strong';
                this.passwordStrengthText = 'Strong password';
            } else {
                this.passwordStrength = 'medium';
                this.passwordStrengthText = 'Medium strength password';
            }
        } else {
            this.passwordStrength = 'weak';
            this.passwordStrengthText = 'Weak password';
        }
        if (this.confirmPassword) {
            this.validateConfirmPassword();
        }
    }

    validateConfirmPassword(): void {
        // Validation happens in getter methods
    }

    calculatePasswordStrength(): void {
        const password = this.newPassword;
        let score = 0;

        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[a-z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;

        if (score <= 2) {
            this.passwordStrength = 'weak';
            this.passwordStrengthText = 'Weak Password';
        } else if (score <= 3) {
            this.passwordStrength = 'medium';
            this.passwordStrengthText = 'Medium Password';
        } else {
            this.passwordStrength = 'strong';
            this.passwordStrengthText = 'Strong Password';
        }
    }

    get newPasswordErrors(): string[] {
        const errors: string[] = [];

        if (!this.newPassword) {
            errors.push('EMPLOYEE.CHANGE_PASSWORD.ERROR_REQUIRED');
            return errors;
        }

        if (this.newPassword.length < 8) {
            errors.push('EMPLOYEE.CHANGE_PASSWORD.ERROR_MIN_LENGTH');
        }

        if (!/[A-Z]/.test(this.newPassword)) {
            errors.push('EMPLOYEE.CHANGE_PASSWORD.ERROR_UPPERCASE');
        }

        if (!/[a-z]/.test(this.newPassword)) {
            errors.push('EMPLOYEE.CHANGE_PASSWORD.ERROR_LOWERCASE');
        }

        if (!/\d/.test(this.newPassword)) {
            errors.push('EMPLOYEE.CHANGE_PASSWORD.ERROR_NUMBER');
        }

        return errors;
    }

    get confirmPasswordErrors(): string[] {
        const errors: string[] = [];

        if (!this.confirmPassword) {
            errors.push('EMPLOYEE.CHANGE_PASSWORD.ERROR_CONFIRM_REQUIRED');
            return errors;
        }

        if (this.newPassword !== this.confirmPassword) {
            errors.push('EMPLOYEE.CHANGE_PASSWORD.ERROR_MISMATCH');
        }

        return errors;
    }

    isFormValid(): boolean {
        return (
            this.newPassword.length >= 8 &&
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(this.newPassword) &&
            this.newPassword === this.confirmPassword
        );
    }

    changePassword(): void {
        if (!this.isFormValid()) {
            return;
        }
        this.loading = true;
        this.errorMessage = '';
        this.employeeAuthService.changePassword(this.sessionId, this.newPassword, this.confirmPassword).subscribe({
            next: (response) => {
                if (this.errorMessageService.isSuccessResponse(response)) {
                    sessionStorage.removeItem('employeeSessionData');
                    this.showSuccessScreen = true;
                    setTimeout(() => {
                        this.router.navigate(['/employee/account/login'], {
                            queryParams: { passwordChanged: 'true' }
                        });
                    }, 500);
                } else {
                    this.errorMessageService.getErrorMessage(response.errorCode).subscribe(message => {
                        this.errorMessage = message;
                    });
                }
            },
            error: (error) => {
                this.errorMessage = error.error?.message || 'Failed to change password';
                this.loading = false;
            },
            complete: () => {
                this.loading = false;
            }
        });
    }
} 